import React, { useCallback, useEffect, useState } from "react";
import {
    ScrollText, RefreshCw, Loader2, Search, Download, ShieldCheck,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Compliance ▸ Signed Consents
 *
 * One row per signed verification consent. Downloadable printable record for
 * each — same document the talent can pull from their own dashboard.
 */

const TYPE_LABEL = {
    identity:   "Identity",
    employment: "Employment",
    education:  "Education",
    reference:  "References",
    background: "Background Check",
};

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function SignedConsents() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [page, setPage] = useState(1);
    const pageSize = 50;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
            if (typeFilter) params.set("type", typeFilter);
            if (q.trim()) params.set("q", q.trim());
            const { data } = await api.get(`/admin/compliance/consents?${params.toString()}`);
            setRows(data.results || []);
            setTotal(data.total || 0);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [q, typeFilter, page]);

    useEffect(() => { load(); }, [load]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    return (
        <div data-testid="admin-signed-consents">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <p className="overline">Compliance</p>
                    <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                        Signed Consents
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Every consent ever signed on Simonara — timestamp, IP, doc version, verbatim
                        authorization text. Download any record as a PDF for auditors or subpoenas.
                    </p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    data-testid="sc-refresh"
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-60"
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            <div className="mt-5 flex flex-wrap gap-2 items-center">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        value={q}
                        onChange={(e) => { setQ(e.target.value); setPage(1); }}
                        placeholder="Search by email"
                        data-testid="sc-filter-q"
                        className="pl-9"
                    />
                </div>
                <Select value={typeFilter || "all"} onValueChange={(v) => { setTypeFilter(v === "all" ? "" : v); setPage(1); }}>
                    <SelectTrigger data-testid="sc-filter-type" className="w-56"><SelectValue placeholder="Any type" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Any type</SelectItem>
                        {Object.entries(TYPE_LABEL).map(([k, l]) => (
                            <SelectItem key={k} value={k}>{l}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <div className="ml-auto text-xs text-muted-foreground">
                    {total.toLocaleString()} record{total === 1 ? "" : "s"} on file
                </div>
            </div>

            <div className="mt-4 rounded-2xl border border-border bg-card overflow-hidden">
                {loading ? (
                    <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
                ) : rows.length === 0 ? (
                    <div data-testid="sc-empty" className="p-12 text-center">
                        <ScrollText className="mx-auto h-8 w-8 text-muted-foreground" />
                        <h3 className="mt-3 text-base font-semibold text-foreground">No consents match.</h3>
                        <p className="mt-1 text-sm text-muted-foreground">Adjust the search / filter above.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/30">
                                    <th className="px-4 py-3 font-semibold">Signer</th>
                                    <th className="px-4 py-3 font-semibold">Type</th>
                                    <th className="px-4 py-3 font-semibold">Signed at (UTC)</th>
                                    <th className="px-4 py-3 font-semibold">IP recorded</th>
                                    <th className="px-4 py-3 font-semibold">Doc version</th>
                                    <th className="px-4 py-3 font-semibold text-right">Record</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr
                                        key={r.consent_id}
                                        data-testid={`sc-row-${r.consent_id}`}
                                        className="border-b border-border/50 last:border-b-0 hover:bg-muted/30"
                                    >
                                        <td className="px-4 py-3">
                                            <div className="text-foreground">{r.email}</div>
                                            <div className="text-[11px] text-muted-foreground font-mono">{(r.user_id || "").slice(0, 12)}</div>
                                        </td>
                                        <td className="px-4 py-3">{TYPE_LABEL[r.verification_type] || r.verification_type}</td>
                                        <td className="px-4 py-3 text-xs text-muted-foreground">
                                            {r.consented_at ? new Date(r.consented_at).toISOString().replace("T", " ").slice(0, 19) : "—"}
                                        </td>
                                        <td className="px-4 py-3 font-mono text-xs">{r.ip || "—"}</td>
                                        <td className="px-4 py-3 text-xs">
                                            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 dark:border-emerald-500/40 px-2 py-0.5 text-emerald-700 dark:text-emerald-300">
                                                <ShieldCheck className="h-3 w-3" /> v{r.doc_version || "1.0"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <a
                                                href={`${API_BASE}/api/admin/verifications/consents/${r.consent_id}/record`}
                                                target="_blank"
                                                rel="noreferrer noopener"
                                                data-testid={`sc-download-${r.consent_id}`}
                                                className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/40 px-2.5 py-1 text-[11px] font-semibold text-emerald-800 dark:text-emerald-300 hover:bg-emerald-100"
                                            >
                                                <Download className="h-3 w-3" /> Download
                                            </a>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {total > pageSize && (
                <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                    <span>Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</span>
                    <div className="flex gap-1">
                        <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="btn-outline px-3 py-1 rounded-full disabled:opacity-40">← Prev</button>
                        <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="btn-outline px-3 py-1 rounded-full disabled:opacity-40">Next →</button>
                    </div>
                </div>
            )}
        </div>
    );
}
